import initSqlJs from 'sql.js';

class Database {
    constructor() {
        this.db = null;
        this.SQL = null;
    }

    async initialize() {
        try {
            // Ініціалізація SQL.js
            this.SQL = await initSqlJs({
                locateFile: file => `https://sql.js.org/dist/${file}`
            });
            
            // Створення нової бази даних в пам'яті
            this.db = new this.SQL.Database();
            
            // Створення структури та наповнення даними
            await this.createSchema();
            await this.populateData();
            
            console.log('Database initialized successfully');
            return true;
        } catch (error) {
            console.error('Database initialization failed:', error);
            return false;
        }
    }

    async createSchema() {
        // Створення таблиць
        const schemas = [
            // MaterialTypes
            `CREATE TABLE MaterialTypes (
                id INTEGER PRIMARY KEY AUTOINCREMENT,
                name VARCHAR(100) NOT NULL UNIQUE,
                description TEXT
            )`,
            
            // Elements
            `CREATE TABLE Elements (
                id INTEGER PRIMARY KEY AUTOINCREMENT,
                symbol VARCHAR(3) NOT NULL UNIQUE,
                name VARCHAR(100) NOT NULL UNIQUE,
                atomic_number INTEGER NOT NULL UNIQUE,
                atomic_mass REAL
            )`,
            
            // Materials
            `CREATE TABLE Materials (
                id INTEGER PRIMARY KEY AUTOINCREMENT,
                name VARCHAR(200) NOT NULL UNIQUE,
                formula VARCHAR(100),
                type_id INTEGER NOT NULL,
                density REAL,
                description TEXT,
                FOREIGN KEY (type_id) REFERENCES MaterialTypes(id)
            )`,
            
            // MaterialComposition
            `CREATE TABLE MaterialComposition (
                material_id INTEGER NOT NULL,
                element_id INTEGER NOT NULL,
                percentage REAL NOT NULL,
                PRIMARY KEY (material_id, element_id),
                FOREIGN KEY (material_id) REFERENCES Materials(id),
                FOREIGN KEY (element_id) REFERENCES Elements(id)
            )`,
            
            // Properties
            `CREATE TABLE Properties (
                id INTEGER PRIMARY KEY AUTOINCREMENT,
                name VARCHAR(100) NOT NULL UNIQUE,
                unit VARCHAR(50),
                category VARCHAR(50)
            )`,
            
            // MaterialProperties
            `CREATE TABLE MaterialProperties (
                material_id INTEGER NOT NULL,
                property_id INTEGER NOT NULL,
                value REAL NOT NULL,
                PRIMARY KEY (material_id, property_id),
                FOREIGN KEY (material_id) REFERENCES Materials(id),
                FOREIGN KEY (property_id) REFERENCES Properties(id)
            )`,
            
            // Applications
            `CREATE TABLE Applications (
                id INTEGER PRIMARY KEY AUTOINCREMENT,
                name VARCHAR(200) NOT NULL UNIQUE,
                industry VARCHAR(100),
                description TEXT
            )`,
            
            // MaterialApplications
            `CREATE TABLE MaterialApplications (
                material_id INTEGER NOT NULL,
                application_id INTEGER NOT NULL,
                suitability VARCHAR(50),
                notes TEXT,
                PRIMARY KEY (material_id, application_id),
                FOREIGN KEY (material_id) REFERENCES Materials(id),
                FOREIGN KEY (application_id) REFERENCES Applications(id)
            )`
        ];

        schemas.forEach(schema => {
            this.db.run(schema);
        });
    }

    async populateData() {
        // MaterialTypes
        this.db.run(`INSERT INTO MaterialTypes (name, description) VALUES
            ('Metal', 'Metallic materials with high electrical and thermal conductivity'),
            ('Polymer', 'Organic materials consisting of long molecular chains'),
            ('Ceramic', 'Inorganic non-metallic materials with high hardness'),
            ('Composite', 'Materials composed of two or more distinct phases'),
            ('Semiconductor', 'Materials with electrical conductivity between conductors and insulators')`);

        // Elements
        this.db.run(`INSERT INTO Elements (symbol, name, atomic_number, atomic_mass) VALUES
            ('Cu', 'Copper', 29, 63.546),
            ('Al', 'Aluminum', 13, 26.982),
            ('Fe', 'Iron', 26, 55.845),
            ('Au', 'Gold', 79, 196.967),
            ('Ag', 'Silver', 47, 107.868),
            ('Zn', 'Zinc', 30, 65.38),
            ('Sn', 'Tin', 50, 118.710),
            ('C', 'Carbon', 6, 12.011),
            ('Si', 'Silicon', 14, 28.085),
            ('O', 'Oxygen', 8, 15.999),
            ('Ni', 'Nickel', 28, 58.693),
            ('Cr', 'Chromium', 24, 51.996),
            ('Ti', 'Titanium', 22, 47.867),
            ('W', 'Tungsten', 74, 183.84),
            ('Pb', 'Lead', 82, 207.2)`);

        // Materials
        this.db.run(`INSERT INTO Materials (name, formula, type_id, density, description) VALUES
            ('Copper', 'Cu', 1, 8960, 'Pure copper metal with excellent electrical conductivity'),
            ('Aluminum', 'Al', 1, 2700, 'Lightweight metal with good strength-to-weight ratio'),
            ('Steel', 'Fe-C', 1, 7850, 'Iron-carbon alloy, most common structural material'),
            ('Stainless Steel', 'Fe-Cr-Ni', 1, 7900, 'Corrosion-resistant steel alloy'),
            ('Brass', 'Cu-Zn', 1, 8500, 'Copper-zinc alloy with good machinability'),
            ('Bronze', 'Cu-Sn', 1, 8800, 'Copper-tin alloy with high wear resistance'),
            ('Titanium', 'Ti', 1, 4506, 'High strength, low density, corrosion resistant metal'),
            ('Gold', 'Au', 1, 19320, 'Noble metal with highest electrical conductivity'),
            ('Silver', 'Ag', 1, 10490, 'Noble metal with highest thermal conductivity'),
            ('Tungsten', 'W', 1, 19250, 'Metal with highest melting point'),
            ('Polyethylene', '(C2H4)n', 2, 950, 'Common thermoplastic polymer'),
            ('Polypropylene', '(C3H6)n', 2, 905, 'Versatile plastic with high chemical resistance'),
            ('PVC', '(C2H3Cl)n', 2, 1380, 'Rigid plastic used in construction'),
            ('Polystyrene', '(C8H8)n', 2, 1050, 'Transparent brittle plastic'),
            ('Nylon', '(C12H22N2O2)n', 2, 1150, 'Strong synthetic fiber'),
            ('Silicon', 'Si', 5, 2329, 'Most common semiconductor material'),
            ('Germanium', 'Ge', 5, 5323, 'Semiconductor used in early transistors'),
            ('Alumina', 'Al2O3', 3, 3950, 'Ceramic with high hardness and thermal stability'),
            ('Silicon Carbide', 'SiC', 3, 3210, 'Very hard ceramic material'),
            ('Carbon Fiber Composite', 'C-Polymer', 4, 1600, 'High strength-to-weight composite')`);

        // MaterialComposition
        this.db.run(`INSERT INTO MaterialComposition (material_id, element_id, percentage) VALUES
            (1, 1, 100.0),
            (2, 2, 100.0),
            (3, 3, 99.0), (3, 8, 1.0),
            (4, 3, 70.0), (4, 12, 18.0), (4, 11, 10.0), (4, 8, 2.0),
            (5, 1, 65.0), (5, 6, 35.0),
            (6, 1, 88.0), (6, 7, 12.0),
            (7, 13, 100.0),
            (8, 4, 100.0),
            (9, 5, 100.0),
            (10, 14, 100.0)`);

        // Properties
        this.db.run(`INSERT INTO Properties (name, unit, category) VALUES
            ('Thermal Conductivity', 'W/mK', 'Physical'),
            ('Electrical Conductivity', 'S/m', 'Physical'),
            ('Melting Point', '°C', 'Physical'),
            ('Boiling Point', '°C', 'Physical'),
            ('Specific Heat', 'J/kgK', 'Physical'),
            ('Tensile Strength', 'MPa', 'Mechanical'),
            ('Yield Strength', 'MPa', 'Mechanical'),
            ('Young Modulus', 'GPa', 'Mechanical'),
            ('Hardness', 'HV', 'Mechanical'),
            ('Elongation', '%', 'Mechanical')`);

        // MaterialProperties
        this.db.run(`INSERT INTO MaterialProperties (material_id, property_id, value) VALUES
            (1, 1, 401), (1, 2, 59600000), (1, 3, 1085), (1, 6, 220), (1, 8, 130),
            (2, 1, 237), (2, 2, 37700000), (2, 3, 660), (2, 6, 310), (2, 8, 69),
            (3, 1, 50), (3, 2, 10000000), (3, 3, 1370), (3, 6, 500), (3, 8, 200),
            (4, 1, 16), (4, 2, 1450000), (4, 3, 1400), (4, 6, 520), (4, 8, 200),
            (7, 1, 22), (7, 2, 2380000), (7, 3, 1668), (7, 6, 880), (7, 8, 116),
            (8, 1, 318), (8, 2, 45200000), (8, 3, 1064), (8, 6, 120), (8, 8, 79),
            (9, 1, 429), (9, 2, 63000000), (9, 3, 962), (9, 6, 170), (9, 8, 83),
            (16, 1, 148), (16, 2, 0.001), (16, 3, 1414), (16, 6, 130), (16, 8, 190)`);

        // Applications
        this.db.run(`INSERT INTO Applications (name, industry, description) VALUES
            ('Electrical Wiring', 'Electronics', 'Conductors for electrical circuits'),
            ('Heat Exchangers', 'HVAC', 'Transfer of heat between fluids'),
            ('Structural Components', 'Construction', 'Load-bearing building elements'),
            ('Aircraft Components', 'Aerospace', 'Parts for aircraft and spacecraft'),
            ('Medical Implants', 'Healthcare', 'Biocompatible implants for body'),
            ('Automotive Parts', 'Automotive', 'Components for vehicles'),
            ('Electronic Packaging', 'Electronics', 'Enclosures and heat sinks'),
            ('Cutting Tools', 'Manufacturing', 'Tools for machining operations'),
            ('Corrosive Environments', 'Chemical', 'Equipment for aggressive media'),
            ('High Temperature Applications', 'Industrial', 'Components operating at high temperatures')`);

        // MaterialApplications
        this.db.run(`INSERT INTO MaterialApplications (material_id, application_id, suitability, notes) VALUES
            (1, 1, 'Excellent', 'Best electrical conductor after silver'),
            (1, 2, 'Excellent', 'High thermal conductivity'),
            (1, 7, 'Good', 'Good for heat dissipation'),
            (2, 4, 'Excellent', 'Lightweight with good strength'),
            (2, 2, 'Good', 'Good thermal conductivity, lighter than copper'),
            (2, 6, 'Excellent', 'Light weight reduces vehicle weight'),
            (3, 3, 'Excellent', 'Most common structural material'),
            (3, 6, 'Excellent', 'High strength and low cost'),
            (3, 8, 'Good', 'With proper heat treatment'),
            (4, 5, 'Excellent', 'Biocompatible and corrosion resistant'),
            (4, 9, 'Excellent', 'Excellent corrosion resistance'),
            (4, 2, 'Good', 'Corrosion resistant heat exchangers'),
            (7, 4, 'Excellent', 'High strength-to-weight ratio'),
            (7, 5, 'Excellent', 'Biocompatible, used in implants'),
            (7, 9, 'Excellent', 'Excellent corrosion resistance')`);

        console.log('Database populated with sample data');
    }

    exportDatabase() {
        if (!this.db) {
            throw new Error('Database not initialized');
        }
        return this.db.export();
    }

    executeQuery(sql, params = []) {
        if (!this.db) {
            throw new Error('Database not initialized');
        }
        
        try {
            const stmt = this.db.prepare(sql);
            if (params.length > 0) {
                stmt.bind(params);
            }
            
            const results = [];
            while (stmt.step()) {
                results.push(stmt.getAsObject());
            }
            stmt.free();
            
            return results;
        } catch (error) {
            console.error('Query execution error:', error);
            throw error;
        }
    }
}

const db = new Database();
export default db;
